/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ChangeEvent, ReactElement, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { strings } from "../../common/Strings/Strings";
import Field from "../../components/ReactHookFormFields/General/Field";
import Select from "../../components/ReactHookFormFields/General/Select/Select";
import CheckBox from "../../components/ReactHookFormFields/General/CheckBox";
import { TabState } from "./WorkerInvoices";
import { useDebouncedState } from "../../hooks/hooks";
import DateFilters from "../../components/DateFilters";
import { Option } from "../../models/Option";
import Modal from "../../components/Modal/Modal";
import Button from "../../components/Button";
import CloseButton from "../../components/CloseButton";
import { defaultInvoiceFilterFormValues } from "./InvoiceListing/InvoiceList";

interface Props {
  tabKey: TabState;
  reloadInvoiceList(): void;
}

export interface InvoicePaymentStatuses {
  resolved?: boolean;
  unresolved?: boolean;
  withdrawn?: boolean;
}

export type SearchCategory = "invoiceNumber" | "totalCost" | "customer" | any;

const InvoiceFilters = (props: Props): ReactElement => {
  const { tabKey, reloadInvoiceList } = props;
  const listFiltersChanged = useDebouncedState<string>("", 600);
  const [searchCategory, setSearchCategory] =
    useState<SearchCategory>("customer");
  const [searchCategoryOptions, setSearchCategoryOptions] = useState<Option[]>(
    []
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [invoiceListFilterCount, setInvoiceListFilterCount] =
    useState<number>(0);
  const { getValues, register, reset, setValue } = useFormContext();

  useEffect(() => {
    reloadInvoiceList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listFiltersChanged.value]);

  useEffect(() => {
    if (tabKey === "active") {
      setSearchCategoryOptions([
        { title: strings.searchToolTip, value: "customer" },
        { title: strings.invoiceNumber, value: "invoiceNumber" },
        { title: strings.totalCost, value: "totalCost" },
      ]);
    } else {
      setSearchCategoryOptions([
        { title: strings.searchToolTip, value: "customer" },
        { title: strings.totalCost, value: "totalCost" },
      ]);
    }
  }, [tabKey]);

  const calculateFilterCounter = () => {
    let cnt = 0;
    const { dateOf, dateTemplate, unresolved, resolved, withdrawn } =
      getValues();

    if (dateOf !== defaultInvoiceFilterFormValues.dateOf) {
      cnt += 1;
    }
    if (dateTemplate !== defaultInvoiceFilterFormValues.dateTemplate) {
      cnt += 1;
    }
    if (
      unresolved !== defaultInvoiceFilterFormValues.unresolved ||
      resolved !== defaultInvoiceFilterFormValues.resolved ||
      withdrawn !== defaultInvoiceFilterFormValues.withdrawn
    ) {
      cnt += 1;
    }
    setInvoiceListFilterCount(cnt);
  };

  return (
    <form>
      <div className="flex items-end flex-wrap">
        <div className="mr-4">
          <Select
            isChooseOption={false}
            label={strings.invoiceSearchCategory}
            name="searchCategory"
            onChange={(e) => {
              setSearchCategory(e.target.value);
              reloadInvoiceList();
            }}
            options={searchCategoryOptions}
            register={register}
          />
        </div>
        <div className="w-64" hidden={searchCategory !== "customer"}>
          <Field
            label={strings.search}
            name="customer"
            register={register}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              listFiltersChanged.setValue(e.target.value);
            }}
            placeholder={strings.searchToolTip}
          />
        </div>
        <div className="w-64" hidden={searchCategory !== "totalCost"}>
          <Field
            label={strings.search}
            name="totalCost"
            register={register}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              listFiltersChanged.setValue(e.target.value);
            }}
            placeholder={strings.totalCost}
            type="number"
          />
        </div>
        <div className="w-64" hidden={searchCategory !== "invoiceNumber"}>
          <Field
            label={strings.search}
            name="invoiceNumber"
            register={register}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              listFiltersChanged.setValue(e.target.value);
            }}
            placeholder={strings.invoiceNumber}
          />
        </div>
        <div className="ml-auto">
          <Button
            onClick={() => {
              setIsFilterModalOpen(true);
            }}
            variant="tertiary"
          >
            {strings.filters}
            {invoiceListFilterCount > 0 && (
              <div
                className="ml-2 bg-sky-500 rounded-full w-5 h-5 flex items-center justify-center"
                id="calendarNumberContainer"
              >
                <span
                  className="text-center font-medium text-white"
                  id="calendarNumber"
                >
                  {invoiceListFilterCount}
                </span>
              </div>
            )}
          </Button>
        </div>
      </div>
      <div className="mt-4" hidden={tabKey !== "active"}>
        <Field
          className="hidden"
          name="pageSize"
          register={register}
          type="number"
        />
        <Field
          className="hidden"
          name="pageNumber"
          register={register}
          type="number"
        />
      </div>
      <Modal
        handleClose={() => {
          setIsFilterModalOpen(false);
        }}
        show={isFilterModalOpen}
      >
        <Modal.Header closeButton={false}>
          <>
            <h3 className="truncate overflow-hidden xl:text-xl font-bold leading-tight text-zinc-800 dark:text-white flex items-center">
              {strings.filters}
            </h3>
            {invoiceListFilterCount > 0 && (
              <div
                className="ml-2 bg-sky-500 rounded-full w-7 h-7 flex items-center justify-center"
                id="calendarNumberContainer"
              >
                <span
                  className="text-center font-medium text-white"
                  id="calendarNumber"
                >
                  {invoiceListFilterCount}
                </span>
              </div>
            )}
          </>
        </Modal.Header>
        <Modal.Body>
          <DateFilters
            onChange={() => {
              calculateFilterCounter();
              reloadInvoiceList();
            }}
          />
          <div hidden={tabKey !== "active"}>
            <Select
              isChooseOption={false}
              label={strings.filterDateType}
              name="dateOf"
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                calculateFilterCounter();
                reloadInvoiceList();
              }}
              options={[
                {
                  title: strings.filterByDateOfIssue,
                  value: "issue",
                },
                {
                  title: strings.filterByDateOfPayment,
                  value: "payment",
                },
              ]}
              register={register}
            />
          </div>
          <div hidden={tabKey !== "active"}>
            <div>
              <label>{strings.state}</label>
            </div>
            <div className="flex items-center space-x-4">
              <div>
                <CheckBox
                  label={strings.notPaid}
                  name="unresolved"
                  onChange={() => {
                    calculateFilterCounter();
                    reloadInvoiceList();
                  }}
                  register={register}
                />
              </div>
              <div>
                <CheckBox
                  label={strings.paid}
                  name="resolved"
                  onChange={() => {
                    calculateFilterCounter();
                    reloadInvoiceList();
                  }}
                  register={register}
                />
              </div>
              <div>
                <CheckBox
                  label={strings.withdrawn}
                  name="withdrawn"
                  onChange={() => {
                    calculateFilterCounter();
                    reloadInvoiceList();
                  }}
                  register={register}
                />
              </div>
            </div>
          </div>
          {invoiceListFilterCount > 0 ? (
            <div>
              <Button
                onClick={() => {
                  setValue("dateOf", defaultInvoiceFilterFormValues.dateOf);
                  setValue(
                    "dateTemplate",
                    defaultInvoiceFilterFormValues.dateTemplate
                  );
                  setValue(
                    "startDate",
                    defaultInvoiceFilterFormValues.startDate
                  );
                  setValue("endDate", defaultInvoiceFilterFormValues.endDate);
                  setValue("resolved", defaultInvoiceFilterFormValues.resolved);
                  setValue(
                    "unresolved",
                    defaultInvoiceFilterFormValues.unresolved
                  );
                  setValue(
                    "withdrawn",
                    defaultInvoiceFilterFormValues.withdrawn
                  );
                  setInvoiceListFilterCount(0);
                  reloadInvoiceList();
                }}
                variant="link"
              >
                {strings.clearFilters}
              </Button>
            </div>
          ) : (
            <></>
          )}
        </Modal.Body>
        <Modal.Footer>
          <CloseButton
            onClick={() => {
              setIsFilterModalOpen(false);
            }}
          />
        </Modal.Footer>
      </Modal>
    </form>
  );
};

export default InvoiceFilters;
